<template>
  <div
    class="draggable-strength"
    :class="{ current: isCurrent, dragging: isBeingDragged, rated: strength.rating !== null }"
    data-test="draggable-strength"
    ref="draggableStrength"
  >
    <div ref="draggableNotch" class="draggable-strength-notch"></div>
    <div class="draggable-strength-inner strength-name-text">
      <span>{{ strength.draggableName }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Draggable } from "gsap/all";
import Debug from "debug";
import { StrengthAndRating } from "../../views/strengths-mapping/strengths-mapping.vue";

const debug = Debug("cs.DraggableStrengthComponent");

const props = defineProps<{
  strength: StrengthAndRating;
  isCurrent: boolean;
}>();

const emit = defineEmits(["dragClick", "dragStart", "dragMove", "dragEnd"]);

// template refs
const draggableStrength = ref<HTMLElement | null>(null);
const draggableNotch = ref<HTMLElement | null>(null);

const draggable = ref<any | null>(null);

const isBeingDragged = ref(false);

function onClick(event: Event) {
  emit("dragClick", draggableStrength.value, props.strength);
}

function onDragStart(event: Event) {
  isBeingDragged.value = true;
  emit("dragStart", draggableStrength.value, props.strength);
  event.preventDefault();
}

function onDrag() {
  emit("dragMove", draggableStrength.value, props.strength);
}

function onDragEnd() {
  emit("dragEnd", draggableStrength.value, props.strength);
  isBeingDragged.value = false;
}

function initializeDragAndDrop(newStrengthItem?: StrengthAndRating, event?: Event) {
  draggableStrength.value?.classList.remove("disabled");
  [draggable.value] = Draggable.create(draggableStrength.value, {
    onDrag,
    onDragStart,
    onDragEnd,
    onClick,
  });
}

onMounted(() => {
  initializeDragAndDrop();
});

onBeforeUnmount(() => {
  if (draggable.value) {
    draggable.value.kill();
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import "./strengths-mapping-variables.less";
@import (reference) "../../styles/bootstrap/variables.less";

@strength-notch-size: 0.3em;
@strength-notch-offset: -@strength-notch-size;
@strength-dragging-scale-factor-mobile: 1.5;
@strength-dragging-scale-factor: 1.2;

.draggable-strength {
  position: absolute;
  bottom: 0;
  left: 0;
  overflow: visible;
  pointer-events: auto;
  user-select: none;
  cursor: grab !important; // Needs to override GSAP style property
  padding-top: 2px;

  &.current {
    z-index: @z-index-current-strength !important; // Ensure the current strength sits above the overlay
  }

  @keyframes wiggle {
    0% {
      transform: translateY(0px);
    }
    5% {
      transform: translateY(5px);
    }
    10% {
      transform: translateY(0px);
    }
    15% {
      transform: translateY(5px);
    }
    20% {
      transform: translateY(0px);
    }
  }

  &.current:not(.dragging) .draggable-strength-inner {
    animation-name: wiggle;
    animation-duration: 4s;
    animation-delay: 0.5s;
    animation-iteration-count: infinite;
  }

  &.current.dragging {
    cursor: grabbing !important;
    z-index: @z-index-current-strength-dragging !important;
  }

  .show-more-info &.rated {
    opacity: 0.4 !important;
  }

  .draggable-strength-inner {
    transition: 0.2s transform;
    background-color: white;
    border: @strength-border-width solid @light-gray;
    color: darkgray;
    text-align: center;
    white-space: nowrap;
    line-height: 0em;
    transform: scale(1); // Needed to force navbars (for inner to be in front of notch)

    span {
      display: block; // Ensures correct central positioning
      margin-left: -200%;
      margin-right: -200%;
    }
  }

  &.current .draggable-strength-inner {
    border-color: #f8f8f8;
    color: @strength-name-color;
    box-shadow: 2px 5px 10px rgba(0, 0, 0, 0.2);
  }

  &.current:not(.rated) .draggable-strength-inner {
    border-width: @unrated-current-strength-border-width;
  }

  &.rated:not(.current) {
    cursor: pointer !important;
  }

  .draggable-strength-notch {
    visibility: hidden;
    position: absolute;
    transition: 0.2s all;
    bottom: @strength-border-width * 2;
    left: calc(50% + @strength-notch-offset + 1px);
    width: 0px;
    height: 0px;
    transform: rotateZ(45deg);
    border: @strength-notch-size solid @bright-pink;
  }

  &.dragging .draggable-strength-notch {
    visibility: visible;
    animation: none;
  }

  &.dragging .draggable-strength-inner {
    transform: scale(@strength-dragging-scale-factor) translate3d(0, 0, 0); // Note: the seemingly redundant translate fixes a webkit bug with borders being trimmed!

    @media @media-query-for-smaller-devices {
      transform: scale(@strength-dragging-scale-factor-mobile) translate3d(0, 0, 0);
    }
  }

  &.dragging .draggable-strength-notch {
    transform: scale(@strength-dragging-scale-factor) rotateZ(45deg);
    bottom: @strength-notch-offset * @strength-dragging-scale-factor;

    @media @media-query-for-smaller-devices {
      transform: scale(@strength-dragging-scale-factor-mobile) rotateZ(45deg);
      bottom: @strength-notch-offset * @strength-dragging-scale-factor-mobile;
    }
  }

  &:not(.rated) .draggable-strength-inner {
    @media @media-query-for-smaller-devices {
      // Extend the draggable area beyond the bounds of the
      // actual strength to make it easier to pick up
      &::before {
        content: "";
        position: absolute;
        top: -2em;
        bottom: -2em;
        left: -2em;
        right: -2em;
      }
    }
  }

  &.disabled {
    cursor: auto !important;
  }
}
</style>
